import React, { useEffect, useMemo, useState } from "react";
import styled, { withTheme } from "styled-components";
import {
  loginRegex,
  FieldsValidation,
  emailRegex,
} from "../../../modules/constants";
import { EmailInput } from "../../ui-library/AuthControls/EmailInput";
import { PasswordInput } from "../../ui-library/AuthControls/PasswordInput";
import { Button } from "../../ui-library/AuthControls/Button";
import AlertUGNX from "../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../ToastsAndAlerts/constants";
import { useTranslation } from "react-i18next";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const NextButton = styled(PrimaryButton)`
  width: 100%;
  margin-top: 3rem;

  @media (max-width: 576px) {
    margin-top: auto;
  }
`;

const LoginUsername = styled.div`
  span {
    vertical-align: text-top;
  }
`;

const LinkButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  display: inline;
  margin: 0;
  padding: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme?.violetEggplant};
  text-align: start;
  :hover {
    text-decoration: underline;
  }
`;

const Login = (props) => {
  const {
    onSubmit,
    errorMessage,
    setErrorMessage,
    onForgotPassword,
    buttonDisabled,
    username,
    setUsername,
    openOnSecondStep,
  } = props;

  const { t } = useTranslation();

  const [password, setPassword] = useState("");
  const [validUsername, setValidUsername] = useState(false);
  const [firstStep, setFirstStep] = useState(true);
  const [loginUsername, setLoginUsername] = useState(null);

  const onLoginSubmit = (e) => {
    e.preventDefault();
    onSubmit(username, password);
  };

  useEffect(() => {
    if (
      (username && !loginRegex.test(username)) ||
      username?.length >= FieldsValidation.email.size ||
      !username
    ) {
      setValidUsername(false);
    } else {
      setValidUsername(true);
    }
  }, [username]);

  const onUsernameChange = (e) => {
    setUsername(e.target.value);
  };

  const onPasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const validFirstStep = useMemo(() => loginRegex.test(username), [username]);

  const onCompleFirstStep = () => {
    if (!emailRegex.test(username)) {
      // only if it is not an email
      const uppercaseUsername = username.toUpperCase();
      setUsername(uppercaseUsername);
      setLoginUsername(uppercaseUsername);
    } else {
      setLoginUsername(username);
    }

    setFirstStep(false);
    setErrorMessage(false);
  };

  const onFirstStepKeyDown = (event) => {
    if (event.key === "Enter" && validFirstStep) {
      onCompleFirstStep();
    }
  };

  const changeUsername = () => {
    setFirstStep(true);
    setLoginUsername(null);
  };

  useEffect(() => {
    if (openOnSecondStep) {
      onCompleFirstStep();
    }
  }, [openOnSecondStep]);

  const loginInputPlaceholder = t("appText.auth.orCaregiverID");

  return (
    <form
      onSubmit={onLoginSubmit}
      className="auth-form"
      data-testid="login-view"
    >
      {firstStep ? (
        <>
          <EmailInput
            data-testid="username-input"
            onChange={onUsernameChange}
            value={username}
            loginInput={loginInputPlaceholder}
            inputError={username && !validUsername}
            onKeyPress={(e) => onFirstStepKeyDown(e)}
          />
          <StyledError>
            {username && !validUsername && (
              <span>
                {t("errorText.auth.login.invalid.emailOrCaregiverID")}
              </span>
            )}
          </StyledError>
          <NextButton
            data-testid="login-next-button"
            onClick={() => onCompleFirstStep()}
            disabled={!validFirstStep}
          >
            {t("appText.next")}
          </NextButton>
        </>
      ) : (
        <>
          <LoginUsername>
            <span className="me-2">{loginUsername}</span>
            <LinkButton
              data-testid="change-login-id"
              type="button"
              onClick={changeUsername}
            >
              {t("appText.auth.changeID")}
            </LinkButton>
          </LoginUsername>
          <PasswordInput
            placeholder={t("appText.auth.password")}
            onChange={onPasswordChange}
            value={password}
            margin="mb-3"
            autoFocus={true}
          />
          {errorMessage && (
            <AlertUGNX type={AlertType.DANGER}>
              <div dangerouslySetInnerHTML={{ __html: errorMessage }}></div>
            </AlertUGNX>
          )}
          <LinkButton
            data-testid="forgot-password"
            type="button"
            onClick={onForgotPassword}
          >
            {`${t("appText.auth.forgotPassword").toLocaleUpperCase()}`}
          </LinkButton>
          <Button
            testid="login-button"
            text={t("appText.auth.login")}
            buttonDisabled={buttonDisabled || firstStep}
          />
        </>
      )}
    </form>
  );
};

export default withTheme(Login);
